/**
 * Admin → Private.
 *
 * Melville is a price list that doesn't move. A private customer is a person
 * who gets quoted, usually gets a discount, and gets a week to pay — so this
 * screen is built around the person, not the car.
 *
 * Three things, in the order the work actually happens:
 *   1. Cars the detailer logged that nobody has claimed yet → say whose it is.
 *   2. Each client: what hasn't been invoiced, and what hasn't been paid.
 *   3. Invoice: tick the cars, take money off the total, send it from Zoho.
 *
 * The discount is applied on the invoice and nowhere else — the detailer's
 * payout comes off the job and must not move because a customer got $40 off.
 *
 * Paid / unpaid is read back from Zoho Books, never typed in here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const NAVY = '#1e3a4a', NAVY_LT = '#4A7A91', LINE = '#e5e7eb', MUTED = '#6b7280';
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d + 'T00:00:00'); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

// Zoho's own vocabulary, kept as-is so what the screen says matches Books.
const STATUS_STYLE = {
  paid:          ['#dcfce7', '#15803d'],
  partially_paid:['#fef3c7', '#92400e'],
  overdue:       ['#fee2e2', '#b91c1c'],
  sent:          ['#e0f2fe', '#0369a1'],
  draft:         ['#f1f5f9', '#475569'],
  void:          ['#f1f5f9', '#475569'],
};

const QUOTE_STATUS = {
  draft:     ['#f1f5f9', '#475569'],
  sent:      ['#e0f2fe', '#0369a1'],
  accepted:  ['#dcfce7', '#15803d'],
  scheduled: ['#ede9fe', '#6d28d9'],
  done:      ['#fef3c7', '#92400e'],
  invoiced:  ['#dcfce7', '#15803d'],
  declined:  ['#fee2e2', '#b91c1c'],
};

export default function PrivatePage() {
  const [clients, setClients]   = useState([]);
  const [orphans, setOrphans]   = useState([]);
  const [quotes, setQuotes]     = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState(null);
  const [editing, setEditing]   = useState(null);   // client object | 'new'
  const [openId, setOpenId]     = useState(null);   // client id whose sheet is open
  const [quoting, setQuoting]   = useState(null);   // quote object | 'new'

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [cs, js, qs, sv] = await Promise.all([
        api.privateClients(),
        api.privateJobs({ unassigned: '1' }),
        api.privateQuotes(),
        api.getPrivateServices(),
      ]);
      setClients(cs); setOrphans(js); setQuotes(qs); setServices(sv);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const owing = clients.reduce((s, c) => s + Number(c.outstanding || 0), 0);
  const notBilled = clients.reduce((s, c) => s + Number(c.unbilled_total || 0), 0);

  return (
    <div style={{ padding: 20, maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: NAVY }}>Private customers</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setQuoting('new')} disabled={!clients.length} style={btnPrimary}>+ New quote</button>
          <button onClick={() => setEditing('new')} style={btnGhost}>+ New client</button>
        </div>
      </div>
      <p style={{ margin: '0 0 18px', fontSize: 12.5, color: MUTED }}>
        Walk-in and private work. Invoices are created and emailed from Zoho Books, and paid/unpaid
        comes back from there.
      </p>

      {err && <div style={errBox}>{err}</div>}

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Stat label="Waiting to be invoiced" value={money(notBilled)} tone="#0369a1" />
        <Stat label="Owed by customers" value={money(owing)} tone={owing > 0 ? '#b91c1c' : '#15803d'} />
      </div>

      {/* 1 — quotes: where a private job starts */}
      {quotes.length > 0 && (
        <Section title={`Quotes (${quotes.length})`}>
          <table style={tbl}>
            <thead><tr style={thRow}>
              <th style={th}>Customer</th><th style={th}>Car</th><th style={th}>Services</th>
              <th style={{ ...th, textAlign: 'right' }}>Total</th>
              <th style={th}>Booked</th><th style={th}>Status</th><th style={th} />
            </tr></thead>
            <tbody>
              {quotes.map(q => (
                <QuoteRow key={q.id} q={q} onChanged={load} onEdit={() => setQuoting(q)} onError={setErr} />
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 10 }}>
            Send goes out as a Zoho estimate. Once it's accepted and has a day, the car shows up in
            Detail Queue for the detailers — with the services, never the prices.
          </div>
        </Section>
      )}

      {/* 2 — cars with no owner yet */}
      {orphans.length > 0 && (
        <Section title={`Cars without a customer (${orphans.length})`}>
          <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
            The detailer logged these but nobody is named. Pick whose car it was so it can be invoiced.
          </div>
          <table style={tbl}>
            <thead><tr style={thRow}>
              <th style={th}>Date</th><th style={th}>Car</th><th style={th}>Work</th>
              <th style={{ ...th, textAlign: 'right' }}>Charge</th><th style={th}>Customer</th>
            </tr></thead>
            <tbody>
              {orphans.map(j => (
                <tr key={j.id} style={{ borderTop: `1px solid ${LINE}` }}>
                  <td style={td}>{j.date}</td>
                  <td style={td}><strong>{j.stock_number}</strong> {j.model}</td>
                  <td style={{ ...td, color: MUTED }}>{(j.lines || []).map(l => l.service).join(', ') || j.job_description}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(j.revenue_full)}</td>
                  <td style={td}>
                    <select
                      defaultValue=""
                      style={{ ...inp, width: 190 }}
                      onChange={async e => {
                        if (!e.target.value) return;
                        try { await api.setPrivateJobClient(j.id, Number(e.target.value)); load(); }
                        catch (ex) { setErr(ex.message); }
                      }}
                    >
                      <option value="">— pick —</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {clients.length === 0 && (
            <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '8px 10px', borderRadius: 6, marginTop: 10 }}>
              No clients yet — add one first with “+ New client”.
            </div>
          )}
        </Section>
      )}

      {/* 2 — the clients */}
      {loading ? (
        <div style={{ color: MUTED, fontSize: 13, padding: 30, textAlign: 'center' }}>Loading…</div>
      ) : clients.length === 0 ? (
        <div style={{ color: MUTED, fontSize: 13, padding: 30, textAlign: 'center' }}>
          No private clients yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {clients.map(c => (
            <ClientCard key={c.id} c={c} onOpen={() => setOpenId(c.id)} onEdit={() => setEditing(c)} />
          ))}
        </div>
      )}

      {editing && (
        <ClientEditor
          client={editing === 'new' ? null : editing}
          onClose={changed => { setEditing(null); if (changed) load(); }}
        />
      )}
      {openId && (
        <ClientSheet clientId={openId} onClose={changed => { setOpenId(null); if (changed) load(); }} />
      )}
      {quoting && (
        <QuoteEditor
          quote={quoting === 'new' ? null : quoting}
          clients={clients}
          services={services}
          onClose={changed => { setQuoting(null); if (changed) load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 12, padding: '12px 16px', minWidth: 180 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tone }}>{value}</div>
    </div>
  );
}

function ClientCard({ c, onOpen, onEdit }) {
  const owes = Number(c.outstanding || 0);
  const late = owes > 0 && c.next_due && c.next_due.slice(0, 10) < today();
  return (
    <div style={{ background: '#fff', border: `1px solid ${late ? '#fca5a5' : LINE}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <strong style={{ fontSize: 15, color: NAVY }}>{c.name}</strong>
        {late && <span style={tagStyle('#fee2e2', '#b91c1c')}>overdue</span>}
      </div>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
        {[c.phone, c.email].filter(Boolean).join(' · ') || 'no contact details'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div>
          <div style={miniLabel}>Not invoiced</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>
            {money(c.unbilled_total)}
            <span style={{ fontSize: 11, fontWeight: 600, color: MUTED }}> · {c.unbilled_jobs} car{Number(c.unbilled_jobs) === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div>
          <div style={miniLabel}>Owes</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: owes > 0 ? '#b91c1c' : '#15803d' }}>
            {money(owes)}
            {owes > 0 && c.next_due && (
              <span style={{ fontSize: 11, fontWeight: 600, color: MUTED }}> · due {String(c.next_due).slice(0, 10)}</span>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={onOpen} style={btnPrimary}>Open</button>
        <button onClick={onEdit} style={btnGhost}>Edit</button>
      </div>
    </div>
  );
}

// ── Quotes ──────────────────────────────────────────────────────────────────
function QuoteRow({ q, onChanged, onEdit, onError }) {
  const [busy, setBusy] = useState(null);
  const [bg, fg] = QUOTE_STATUS[q.status] || QUOTE_STATUS.draft;

  const act = async (key, fn) => {
    setBusy(key); onError(null);
    try { await fn(); onChanged(); }
    catch (e) { onError(e.message); }
    finally { setBusy(null); }
  };

  const booked = q.scheduled_date ? String(q.scheduled_date).slice(0, 10) : '';
  const canInvoice = q.status === 'done' && !q.invoice_number;

  return (
    <tr style={{ borderTop: `1px solid ${LINE}` }}>
      <td style={td}><strong>{q.client_name}</strong></td>
      <td style={td}>{[q.stock_number, q.model].filter(Boolean).join(' ') || '—'}</td>
      <td style={{ ...td, color: MUTED }}>{(q.lines || []).map(l => l.service).join(', ')}</td>
      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
        {money(q.total)}
        {Number(q.discount) > 0 && (
          <div style={{ fontSize: 10.5, fontWeight: 600, color: '#15803d' }}>−{money(q.discount)} off</div>
        )}
      </td>
      <td style={td}>
        {/* Booking the day is what puts the car in front of the detailers. */}
        <input
          type="date" value={booked} style={{ ...inp, width: 140, padding: '5px 7px', fontSize: 12 }}
          disabled={['draft', 'declined', 'invoiced'].includes(q.status)}
          onChange={e => act('book', () => api.patchPrivateQuote(q.id, {
            scheduled_date: e.target.value || null,
            status: e.target.value ? 'scheduled' : 'accepted',
          }))}
        />
      </td>
      <td style={td}>
        <span style={tagStyle(bg, fg)}>{q.status}</span>
        {q.invoice_number && <div style={{ fontSize: 10.5, color: MUTED, marginTop: 2 }}>{q.invoice_number}</div>}
        {Number(q.job_count) > 0 && (
          <div style={{ fontSize: 10.5, color: MUTED, marginTop: 2 }}>{q.job_count} job(s) logged</div>
        )}
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {q.status === 'draft' && (
          <button style={btnPrimarySm} onClick={onEdit}>Edit</button>
        )}
        {['draft', 'sent'].includes(q.status) && (
          <button style={btnPrimarySm} disabled={busy === 'send'}
                  onClick={() => act('send', () => api.sendPrivateQuote(q.id))}>
            {busy === 'send' ? '…' : q.zoho_estimate_id ? 'Resend' : 'Send'}
          </button>
        )}
        {q.zoho_estimate_id && ['sent', 'draft'].includes(q.status) && (
          <button style={btnPrimarySm} disabled={busy === 'sync'}
                  onClick={() => act('sync', () => api.syncPrivateQuote(q.id))}>
            {busy === 'sync' ? '…' : 'Check'}
          </button>
        )}
        {['draft', 'sent'].includes(q.status) && (
          <button style={btnPrimarySm} disabled={busy === 'acc'}
                  onClick={() => act('acc', () => api.patchPrivateQuote(q.id, { status: 'accepted' }))}>
            Accepted
          </button>
        )}
        {canInvoice && (
          <button style={btnPrimarySm} disabled={busy === 'inv'}
                  onClick={() => act('inv', () => api.createPrivateInvoice({ quote_id: q.id }))}>
            {busy === 'inv' ? 'Creating…' : 'Invoice'}
          </button>
        )}
        {!q.invoice_number && (
          <button style={btnDanger} title="Delete this quote"
                  onClick={() => { if (window.confirm('Delete this quote?')) act('del', () => api.deletePrivateQuote(q.id)); }}>🗑</button>
        )}
      </td>
    </tr>
  );
}

function QuoteEditor({ quote, clients, services, onClose }) {
  const [clientId, setClientId] = useState(quote?.client_id || clients[0]?.id || '');
  const [plate, setPlate]       = useState(quote?.stock_number || '');
  const [model, setModel]       = useState(quote?.model || '');
  const [discount, setDisc]     = useState(quote?.discount ? String(quote.discount) : '');
  const [notes, setNotes]       = useState(quote?.notes || '');
  const [picked, setPicked]     = useState(() => {
    const m = {};
    (quote?.lines || []).forEach(l => { m[l.service] = { rate: String(l.rate), qty: String(l.qty || 1) }; });
    return m;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const toggle = (s) => setPicked(p => {
    const next = { ...p };
    if (next[s.service_name]) delete next[s.service_name];
    else next[s.service_name] = { rate: String(s.charge_ex_gst), qty: '1' };
    return next;
  });

  const lines = Object.entries(picked).map(([service, v]) => ({
    service, rate: Number(v.rate) || 0, qty: Number(v.qty) || 1,
  }));
  const gross = lines.reduce((s, l) => s + l.rate * l.qty, 0);
  const disc  = Math.max(0, Number(discount) || 0);
  const net   = Math.max(0, gross - disc);

  async function save() {
    setBusy(true); setErr(null);
    const body = { client_id: Number(clientId), stock_number: plate.trim().toUpperCase(), model: model.trim(), lines, discount: disc, notes: notes.trim() || null };
    try {
      if (quote) await api.updatePrivateQuote(quote.id, body);
      else       await api.createPrivateQuote(body);
      onClose(true);
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <Modal wide onClose={() => onClose(false)}>
      <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 800, color: NAVY }}>
        {quote ? 'Edit quote' : 'New quote'}
      </h3>
      {err && <div style={errBox}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
        <Field label="Customer">
          <select style={inp} value={clientId} onChange={e => setClientId(e.target.value)}>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Plate">
          <input style={inp} value={plate} onChange={e => setPlate(e.target.value)} placeholder="1E0X785" />
        </Field>
        <Field label="Car">
          <input style={inp} value={model} onChange={e => setModel(e.target.value)} placeholder="Yaris" />
        </Field>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        Services
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {services.map(s => {
          const on = !!picked[s.service_name];
          return (
            <span key={s.id} onClick={() => toggle(s)} style={{
              cursor: 'pointer', fontSize: 12.5, fontWeight: 700, padding: '6px 11px', borderRadius: 999,
              border: `1.5px solid ${on ? NAVY : LINE}`, background: on ? NAVY : '#fff', color: on ? '#fff' : NAVY,
            }}>
              {s.service_name} · {money(s.charge_ex_gst)}
            </span>
          );
        })}
      </div>

      {/* The price can be moved per line — a quote is a negotiation, not a
          price list lookup. */}
      {lines.length > 0 && (
        <table style={{ ...tbl, marginBottom: 12 }}>
          <thead><tr style={thRow}>
            <th style={th}>Service</th>
            <th style={{ ...th, width: 90 }}>Qty</th>
            <th style={{ ...th, width: 120 }}>Rate ex GST</th>
            <th style={{ ...th, textAlign: 'right' }}>Amount</th>
          </tr></thead>
          <tbody>
            {lines.map(l => (
              <tr key={l.service} style={{ borderTop: `1px solid ${LINE}` }}>
                <td style={td}>{l.service}</td>
                <td style={td}>
                  <input type="number" min="0.5" step="0.5" style={{ ...inp, width: 80 }}
                         value={picked[l.service].qty}
                         onChange={e => setPicked(p => ({ ...p, [l.service]: { ...p[l.service], qty: e.target.value } }))} />
                </td>
                <td style={td}>
                  <input type="number" min="0" step="0.01" style={{ ...inp, width: 110 }}
                         value={picked[l.service].rate}
                         onChange={e => setPicked(p => ({ ...p, [l.service]: { ...p[l.service], rate: e.target.value } }))} />
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(l.rate * l.qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 12 }}>
        <Field label="Discount $">
          <input type="number" min="0" step="0.01" style={inp} value={discount}
                 onChange={e => setDisc(e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="Subject / note on the quote" full>
          <input style={inp} value={notes} onChange={e => setNotes(e.target.value)}
                 placeholder="Referred by friend" />
        </Field>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: MUTED }}>
          {money(gross)}{disc > 0 && <> − {money(disc)}</>} ={' '}
          <strong style={{ color: NAVY, fontSize: 16 }}>{money(net)}</strong> ex GST
          <span style={{ fontSize: 11.5 }}> · {money(net * 1.1)} inc GST</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onClose(false)} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={busy || !lines.length || !clientId || net <= 0} style={btnPrimary}>
            {busy ? 'Saving…' : 'Save quote'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Client editor ───────────────────────────────────────────────────────────
function ClientEditor({ client, onClose }) {
  const [f, setF]         = useState(client || { name: '', phone: '', email: '', address: '', notes: '' });
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      if (client) await api.updatePrivateClient(client.id, f);
      else        await api.createPrivateClient(f);
      onClose(true);
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <Modal onClose={() => onClose(false)}>
      <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 800, color: NAVY }}>
        {client ? client.name : 'New private client'}
      </h3>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Name" full>
          <input style={inp} value={f.name || ''} onChange={e => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Phone">
          <input style={inp} value={f.phone || ''} onChange={e => setF({ ...f, phone: e.target.value })} />
        </Field>
        <Field label="Email">
          <input style={inp} value={f.email || ''} onChange={e => setF({ ...f, email: e.target.value })} />
        </Field>
        <Field label="Address" full>
          <input style={inp} value={f.address || ''} onChange={e => setF({ ...f, address: e.target.value })} />
        </Field>
        <Field label="Notes" full>
          <textarea style={{ ...inp, minHeight: 60 }} value={f.notes || ''} onChange={e => setF({ ...f, notes: e.target.value })} />
        </Field>
      </div>
      {client?.zoho_contact_id && (
        <div style={{ fontSize: 11.5, color: MUTED, marginTop: 10 }}>
          Linked to Zoho contact {client.zoho_contact_id}. Changing the name here does not rename it there.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={() => onClose(false)} style={btnGhost}>Cancel</button>
        <button onClick={save} disabled={busy || !String(f.name || '').trim()} style={btnPrimary}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ── One client: their cars, their invoices ──────────────────────────────────
function ClientSheet({ clientId, onClose }) {
  const [data, setData]     = useState(null);
  const [err, setErr]       = useState(null);
  const [picked, setPicked] = useState({});          // job id → true
  const [discount, setDisc] = useState('');
  const [date, setDate]     = useState(today());
  const [due, setDue]       = useState(addDays(today(), 7));
  const [busy, setBusy]     = useState(null);        // 'invoice' | `sync-${id}` | …
  const [changed, setChanged] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.privateClient(clientId)); }
    catch (e) { setErr(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const jobs = (data?.jobs || []).filter(j => !j.private_invoice_id);
  const chosen = jobs.filter(j => picked[j.id]);
  const gross = chosen.reduce((s, j) => s + Number(j.revenue_full || 0), 0);
  const disc  = Math.max(0, Number(discount) || 0);
  const net   = Math.max(0, gross - disc);

  async function createInvoice() {
    setBusy('invoice'); setErr(null);
    try {
      await api.createPrivateInvoice({
        client_id: clientId,
        job_ids: chosen.map(j => j.id),
        discount: disc,
        date, due_date: due,
      });
      setPicked({}); setDisc(''); setChanged(true);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  }

  async function act(key, fn) {
    setBusy(key); setErr(null);
    try { await fn(); setChanged(true); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  }

  return (
    <Modal wide onClose={() => onClose(changed)}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: NAVY }}>{data?.name || 'Loading…'}</h3>
        <button onClick={() => onClose(changed)} style={btnGhost}>Close</button>
      </div>
      {err && <div style={errBox}>{err}</div>}

      <Section title={`Not yet invoiced (${jobs.length})`}>
        {jobs.length === 0 ? (
          <div style={{ fontSize: 12.5, color: MUTED }}>Nothing waiting. Every car of theirs is on an invoice.</div>
        ) : (
          <>
            <table style={tbl}>
              <thead><tr style={thRow}>
                <th style={{ ...th, width: 28 }} />
                <th style={th}>Date</th><th style={th}>Car</th><th style={th}>Work</th>
                <th style={{ ...th, textAlign: 'right' }}>Charge</th>
                <th style={{ ...th, width: 34 }} />
              </tr></thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id} style={{ borderTop: `1px solid ${LINE}` }}>
                    <td style={td}>
                      <input type="checkbox" checked={!!picked[j.id]}
                             onChange={e => setPicked({ ...picked, [j.id]: e.target.checked })} />
                    </td>
                    <td style={td}>{j.date}</td>
                    <td style={td}><strong>{j.stock_number}</strong> {j.model}</td>
                    <td style={{ ...td, color: MUTED }}>
                      {(j.lines || []).map(l => l.service).join(', ') || j.job_description}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(j.revenue_full)}</td>
                    <td style={td}>
                      {/* Attaching a car to the wrong person is a two-tap
                          mistake; until now undoing it wasn't possible at
                          all. The car goes back to "cars without a customer",
                          where it can be given to the right one. */}
                      <button
                        title="Not this customer's car — send it back to the unassigned list"
                        disabled={busy === `drop-${j.id}`}
                        onClick={() => act(`drop-${j.id}`, async () => {
                          await api.setPrivateJobClient(j.id, null);
                          setPicked(p => { const n = { ...p }; delete n[j.id]; return n; });
                        })}
                        style={btnDanger}
                      >{busy === `drop-${j.id}` ? '…' : '✕'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {chosen.length > 0 && (
              <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 12, paddingTop: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 12 }}>
                  <Field label="Invoice date">
                    <input type="date" style={inp} value={date}
                           onChange={e => { setDate(e.target.value); setDue(addDays(e.target.value, 7)); }} />
                  </Field>
                  <Field label="Due (7 days)">
                    <input type="date" style={inp} value={due} onChange={e => setDue(e.target.value)} />
                  </Field>
                  <Field label="Discount $">
                    <input type="number" min="0" step="0.01" style={inp} value={discount}
                           onChange={e => setDisc(e.target.value)} placeholder="0.00" />
                  </Field>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 12.5, color: MUTED }}>
                    {chosen.length} car{chosen.length === 1 ? '' : 's'} · {money(gross)}
                    {disc > 0 && <> − {money(disc)} discount</>} ={' '}
                    <strong style={{ color: NAVY, fontSize: 15 }}>{money(net)}</strong> ex GST
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* For the ones that were written by hand in Zoho before
                        the app could do it — linking beats making a second
                        invoice for work that's already been paid for. */}
                    <button
                      style={btnGhost}
                      disabled={busy === 'link'}
                      onClick={() => {
                        const n = window.prompt('Invoice number in Zoho Books (e.g. Inv_RETAIL_26W38_EBRAZ):');
                        if (!n) return;
                        act('link', () => api.linkPrivateInvoice({
                          client_id: clientId, job_ids: chosen.map(j => j.id), invoice_number: n.trim(),
                        })).then(() => setPicked({}));
                      }}
                    >{busy === 'link' ? 'Looking up…' : 'Already invoiced'}</button>
                    <button onClick={createInvoice} disabled={busy === 'invoice' || net <= 0} style={btnPrimary}>
                      {busy === 'invoice' ? 'Creating in Zoho…' : 'Create invoice in Zoho'}
                    </button>
                  </div>
                </div>
                {disc > 0 && (
                  <div style={{ fontSize: 11.5, color: MUTED, marginTop: 8 }}>
                    The discount comes off the customer's invoice only — nobody's payout changes.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Section>

      <Section title={`Invoices (${(data?.invoices || []).length})`}>
        {(data?.invoices || []).length === 0 ? (
          <div style={{ fontSize: 12.5, color: MUTED }}>No invoices yet.</div>
        ) : (
          <table style={tbl}>
            <thead><tr style={thRow}>
              <th style={th}>Number</th><th style={th}>Date</th><th style={th}>Due</th>
              <th style={{ ...th, textAlign: 'right' }}>Total</th>
              <th style={{ ...th, textAlign: 'right' }}>Owing</th>
              <th style={th}>Status</th><th style={th} />
            </tr></thead>
            <tbody>
              {(data?.invoices || []).map(i => {
                const [bg, fg] = STATUS_STYLE[i.status] || STATUS_STYLE.draft;
                const overdue = Number(i.balance) > 0 && i.due_date && String(i.due_date).slice(0, 10) < today();
                return (
                  <tr key={i.id} style={{ borderTop: `1px solid ${LINE}` }}>
                    <td style={td}>
                      {i.zoho_invoice_id ? (
                        <a href={`https://books.zoho.com.au/app#/invoices/${i.zoho_invoice_id}`}
                           target="_blank" rel="noreferrer" style={{ color: NAVY_LT, fontWeight: 700 }}>
                          {i.invoice_number || 'view'}
                        </a>
                      ) : (i.invoice_number || '—')}
                    </td>
                    <td style={td}>{String(i.date || '').slice(0, 10)}</td>
                    <td style={{ ...td, color: overdue ? '#b91c1c' : undefined, fontWeight: overdue ? 700 : 400 }}>
                      {String(i.due_date || '').slice(0, 10)}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(i.total)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(i.balance)}</td>
                    <td style={td}>
                      <span style={tagStyle(bg, fg)}>{String(i.status || '').replace('_', ' ') || 'draft'}</span>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button style={btnPrimarySm} disabled={busy === `sync-${i.id}`}
                              onClick={() => act(`sync-${i.id}`, () => api.syncPrivateInvoice(i.id))}>
                        {busy === `sync-${i.id}` ? '…' : 'Refresh'}
                      </button>
                      <button style={btnPrimarySm} disabled={busy === `mail-${i.id}`}
                              onClick={() => act(`mail-${i.id}`, () => api.emailPrivateInvoice(i.id))}>
                        {busy === `mail-${i.id}` ? '…' : i.emailed_at ? 'Resend' : 'Email'}
                      </button>
                      <button style={btnDanger} title="Unlink these cars so they can be invoiced again"
                              onClick={() => {
                                if (!window.confirm('Unlink this invoice? The cars go back to “not invoiced”. The invoice stays in Zoho — delete it there too if you meant to cancel it.')) return;
                                act(`del-${i.id}`, () => api.deletePrivateInvoice(i.id));
                              }}>🗑</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 11.5, color: MUTED, marginTop: 10 }}>
          “Owing” and the status come from Zoho Books. Record the payment there and hit Refresh.
        </div>
      </Section>
    </Modal>
  );
}

// ── building blocks (same look as the Customers tab) ────────────────────────
function Modal({ children, onClose, wide }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 600, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: wide ? 860 : 480, padding: 22, marginTop: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {children}
      </div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 14, marginBottom: 14, background: '#fff' }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: NAVY_LT, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <label style={miniLabel}>{label}</label>
      {children}
    </div>
  );
}

const miniLabel  = { display: 'block', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 };
const inp        = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const btnPrimary = { padding: '8px 16px', borderRadius: 8, border: 'none', background: NAVY, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' };
const btnPrimarySm = { padding: '4px 10px', borderRadius: 6, border: 'none', background: NAVY, color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', marginRight: 4 };
const btnGhost   = { padding: '7px 14px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', color: NAVY, fontWeight: 600, fontSize: 12.5, cursor: 'pointer' };
const btnDanger  = { padding: '4px 8px', borderRadius: 5, border: '1px solid #fca5a5', background: '#fff1f1', color: '#b91c1c', fontSize: 11, cursor: 'pointer' };
const errBox     = { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 };
const tbl        = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 };
const thRow      = { background: '#f8fafc', color: '#475569', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 };
const th         = { textAlign: 'left', padding: '6px 8px' };
const td         = { padding: '6px 8px', color: '#0f172a' };
const tagStyle   = (bg, fg) => ({ fontSize: 10, fontWeight: 700, color: fg, background: bg, padding: '2px 7px', borderRadius: 999, textTransform: 'capitalize' });
